/** Guards against `Intl.DateTimeFormat.format` throwing `RangeError: Invalid time value` — falls
 * back to the original string (or a generic label for a bad `Date` object) instead of crashing the
 * page over a malformed/empty date field. */
function toValidDate(date: Date | string): Date | null {
  const d = typeof date === 'string' ? new Date(date) : date
  return Number.isNaN(d.getTime()) ? null : d
}

/** This app only ever operates in India — dates/times are pinned to `Asia/Kolkata` rather than
 * left to the ambient runtime zone, which otherwise differs between Next.js's server-render pass
 * (container, typically UTC) and the browser's own OS zone at hydration, and would show every
 * staff/investor a different wall-clock time for the same instant depending on where they are. */
const APP_TIME_ZONE = 'Asia/Kolkata'

export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = toValidDate(date)
  if (!d) return typeof date === 'string' ? date : 'Invalid date'
  return new Intl.DateTimeFormat('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: APP_TIME_ZONE,
    ...options,
  }).format(d)
}

/** Date + time, for timestamp fields where the time of day is meaningful (e.g. "when was this
 * updated/logged/uploaded") — as opposed to `formatDate`, for pure-date fields (e.g. a follow-up
 * or "client since" date) where a time of day was never collected. */
export function formatDateTime(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = toValidDate(date)
  if (!d) return typeof date === 'string' ? date : 'Invalid date'
  return new Intl.DateTimeFormat('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: APP_TIME_ZONE,
    ...options,
  }).format(d)
}

/** Time of day only (e.g. a consultation slot option) — same locked locale/`hour12`/`timeZone` as
 * `formatDateTime`, just without the date portion. Never use the device's ambient locale
 * (`toLocaleTimeString(undefined, ...)`) for this: some regions default to 24-hour time, so the same
 * code would render "14:30" on one phone and "2:30 PM" on another depending on device locale alone. */
export function formatTime(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = toValidDate(date)
  if (!d) return typeof date === 'string' ? date : 'Invalid date'
  return new Intl.DateTimeFormat('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: APP_TIME_ZONE,
    ...options,
  }).format(d)
}

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/** "just now", "5 minutes ago", "3 hours ago", "2 days ago" — and a plain date from 30 days on, where
 * a relative phrase stops being useful. A time slightly in the future (clock skew between the
 * server and this browser) reads as "just now" rather than a negative duration. Falls back to the
 * raw string / a generic label on an invalid date, same as the other formatters here. */
export function formatRelativeTime(date: Date | string, now: Date | number = new Date()): string {
  const d = toValidDate(date)
  if (!d) return typeof date === 'string' ? date : 'Invalid date'

  const diff = (typeof now === 'number' ? now : now.getTime()) - d.getTime()
  if (diff < MINUTE_MS) return 'just now'

  const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'} ago`
  if (diff < HOUR_MS) return plural(Math.floor(diff / MINUTE_MS), 'minute')
  if (diff < DAY_MS) return plural(Math.floor(diff / HOUR_MS), 'hour')
  if (diff < 30 * DAY_MS) return plural(Math.floor(diff / DAY_MS), 'day')
  return formatDate(d)
}
